import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { scheduleModalCleanup } from './utils/modalCleanup';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import AdvancedOnlyRoute from './components/AdvancedOnlyRoute';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import DataReadOnlyBanner from './components/DataReadOnlyBanner';
import SlackChat from './components/SlackChat';
import Login from './pages/Login';
import Register from './pages/Register';
import AcceptInvite from './pages/AcceptInvite';
import Dashboard from './pages/Dashboard';
import Overview from './pages/Overview';
import Depots from './pages/Depots';
import Customers from './pages/Customers';
import Staff from './pages/Staff';
import NominalCodes from './pages/NominalCodes';
import Suppliers from './pages/Suppliers';
import SalesCategories from './pages/SalesCategories';
import SalesItems from './pages/SalesItems';
import CustomerSites from './pages/CustomerSites';
import CustomerContacts from './pages/CustomerContacts';
import HireCategories from './pages/HireCategories';
import HireCategoryGroups from './pages/HireCategoryGroups';
import HireRates from './pages/HireRates';
import HireStock from './pages/HireStock';
import HireStockBulk from './pages/HireStockBulk';
import FleetReminders from './pages/FleetReminders';
import InspectionTests from './pages/InspectionTests';
import Inclusions from './pages/Inclusions';
import FleetWizard from './pages/FleetWizard';
import UserManagement from './pages/UserManagement';
import ImportExport from './pages/ImportExport';
import ImportFailures from './pages/ImportFailures';
import ValidationFailures from './pages/ValidationFailures';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import TestModal from './pages/TestModal';
import PricingAgreements from './pages/PricingAgreements';
import PricingAgreementCustomerSites from './pages/PricingAgreementCustomerSites';
import PricingAgreementHireRates from './pages/PricingAgreementHireRates';
import PricingAgreementSalesPrices from './pages/PricingAgreementSalesPrices';
import PricingAgreementTransportRates from './pages/PricingAgreementTransportRates';
import SalesItemsIndividual from './pages/SalesItemsIndividual';
import CustomerEquipment from './pages/CustomerEquipment';
import FleetServiceHistory from './pages/FleetServiceHistory';
import MeterReadings from './pages/MeterReadings';
import LiveContracts from './pages/LiveContracts';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return user ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return user ? <Navigate to="/dashboard" /> : children;
}

function LogoutRoute() {
  const { logout, loading } = useAuth();
  const [done, setDone] = React.useState(false);

  // Wait for the boot-time auth check to settle, THEN log out, THEN navigate.
  // Logging out while /auth/me is still in flight races its setUser(user)
  // against logout's setUser(null) - if /auth/me lands last, the user is
  // "re-logged-in" in memory and PublicRoute bounces /login -> /dashboard.
  React.useEffect(() => {
    if (loading) {
      return undefined;
    }
    let active = true;
    (async () => {
      await logout();
      if (active) {
        setDone(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [logout, loading]);

  return done ? <Navigate to="/login" /> : <div className="loading">Logging out...</div>;
}

function AppLayout({ children }) {
  const location = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    scheduleModalCleanup();
  }, [location.pathname]);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, []);

  const updateUnreadCount = useCallback((count) => {
    setUnreadCount(count);
  }, []);

  return (
    <>
      <Navbar onToggleChat={toggleChat} isChatOpen={isChatOpen} unreadCount={unreadCount} />
      <div className="d-flex">
        <Sidebar />
        <div className="main-content-with-sidebar flex-grow-1">
          <DataReadOnlyBanner />
          {children}
        </div>
      </div>
      <SlackChat
        isOpen={isChatOpen}
        onToggle={toggleChat}
        onUnreadCountChange={updateUnreadCount}
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <ViewModeProvider>
        <Router>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/accept-invite/:token"
              element={
                <PublicRoute>
                  <AcceptInvite />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/overview"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Overview />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet-wizard"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FleetWizard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/depots"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Depots />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Customers />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Staff />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/nominal-codes"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <NominalCodes />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Suppliers />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-categories"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SalesCategories />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-items"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SalesItems />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-sites"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CustomerSites />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-contacts"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CustomerContacts />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hire-categories"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HireCategories />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hire-rates"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HireRates />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hire-category-groups"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HireCategoryGroups />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hire-stock"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HireStock />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hire-stock-bulk"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <HireStockBulk />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet-reminders"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FleetReminders />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inspection-tests"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <InspectionTests />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inclusions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Inclusions />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing-agreements"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <PricingAgreements />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing-agreement-customer-sites"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <PricingAgreementCustomerSites />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing-agreement-hire-rates"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <PricingAgreementHireRates />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing-agreement-sales-prices"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <PricingAgreementSalesPrices />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pricing-agreement-transport-rates"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <PricingAgreementTransportRates />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-items-individual"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <SalesItemsIndividual />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-equipment"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <CustomerEquipment />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fleet-service-history"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <FleetServiceHistory />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/meter-readings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <MeterReadings />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-contracts"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdvancedOnlyRoute>
                      <LiveContracts />
                    </AdvancedOnlyRoute>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/import-export"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ImportExport />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/import-failures/:importLogId"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ImportFailures />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/validation-failures"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ValidationFailures />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <UserManagement />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Profile />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/logout" element={<LogoutRoute />} />
            <Route path="/test-modal" element={<TestModal />} />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Router>
      </ViewModeProvider>
    </AuthProvider>
  );
}

export default App;
